import React from 'react';
import { Star, CheckCircle2, Quote } from 'lucide-react';
import { useLang } from '../context/LanguageContext';
import { testimonials, translations, faqs } from '../mock';
import { SectionHeading } from './UpcomingAndHow';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

export function DevoteeCorner() {
  const { t, lang } = useLang();
  return (
    <section id="corner" className="py-12 md:py-16 px-4 sm:px-6">
      <div className="max-w-7xl mx-auto">
        <SectionHeading subtitle={t(translations.sections.cornerSub)}>
          {t(translations.sections.corner)}
        </SectionHeading>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {testimonials.map((tt) => (
            <div key={tt.id} className="relative bg-white rounded-2xl p-5 md:p-6 border border-orange-100 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition group">
              <Quote className="absolute top-4 right-4 w-6 h-6 md:w-8 md:h-8 text-orange-200" />
              <div className="flex items-center gap-3 mb-4">
                <img src={tt.avatar} alt={tt.name} className="w-12 h-12 md:w-14 md:h-14 rounded-full object-cover border-2 border-orange-200 shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h4 className="font-semibold text-sm md:text-base text-stone-900 truncate">{tt.name}</h4>
                    <CheckCircle2 className="w-3.5 h-3.5 md:w-4 md:h-4 text-green-600 shrink-0" />
                  </div>
                  <p className="text-[11px] md:text-xs text-stone-500">{lang === 'en' ? 'Via' : 'से'} {tt.via}</p>
                </div>
              </div>
              <div className="flex gap-0.5 mb-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="w-3 h-3 md:w-3.5 md:h-3.5 fill-amber-400 text-amber-400" />
                ))}
              </div>
              <p className="text-xs md:text-sm text-stone-700 leading-relaxed italic">“{lang === 'en' ? tt.text_en : tt.text_hi}”</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export function FAQs() {
  const { t, lang } = useLang();
  return (
    <section id="faq" className="py-12 md:py-16 px-4 sm:px-6 bg-gradient-to-b from-transparent via-orange-50/40 to-transparent">
      <div className="max-w-3xl mx-auto">
        <SectionHeading>{t(translations.sections.faqs)}</SectionHeading>
        <Accordion type="single" collapsible className="space-y-3">
          {faqs.map((f, i) => (
            <AccordionItem key={i} value={`item-${i}`} className="bg-white rounded-xl border border-orange-100 px-4 md:px-5 shadow-sm data-[state=open]:shadow-md transition">
              <AccordionTrigger className="text-left font-semibold text-sm md:text-base text-stone-900 hover:no-underline hover:text-orange-700 py-4">
                {lang === 'en' ? f.q_en : f.q_hi}
              </AccordionTrigger>
              <AccordionContent className="text-stone-600 text-xs md:text-sm leading-relaxed pb-4">
                {lang === 'en' ? f.a_en : f.a_hi}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

export function Subscribe() {
  const { t, lang } = useLang();
  return (
    <section className="py-12 md:py-16 px-4 sm:px-6">
      <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-orange-100 via-amber-50 to-orange-100 rounded-3xl p-8 md:p-14 border border-orange-200 shadow-inner relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-orange-400 via-red-500 to-orange-400" />
        <div className="absolute -top-10 -right-10 w-40 h-40 bg-orange-200/40 rounded-full blur-3xl" />
        <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-amber-200/40 rounded-full blur-3xl" />
        <SectionHeading>{t(translations.sections.subscribe)}</SectionHeading>
        <p className="-mt-4 text-orange-800 font-semibold mb-2">{t(translations.sections.subscribeSub)}</p>
        <p className="text-sm md:text-base text-stone-700 max-w-xl mx-auto mb-6">{t(translations.sections.subscribeDesc)}</p>
        <a
          href="https://wa.me/919525141736"
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1ea955] text-white px-6 md:px-8 py-3 md:py-3.5 rounded-full font-semibold shadow-lg transition text-sm md:text-base transform hover:scale-105"
        >
          💬 {t(translations.sections.subscribeBtn)}
        </a>
      </div>
    </section>
  );
}
